import {
  applyFilterOperator,
  FILTER_ABSENT,
  type TFilterLeafOperator,
} from '../../filter/services/filter-dsl.compiler';
import type { IFlowNodeContext } from '../interfaces/node-context.interface';
import {
  EConditionKind,
  EConditionOp,
  type TFlowCondition,
  type TFlowConditionForm,
} from '../schemas/flow-condition.schema';
import { resolveContextPath } from './flow-template';

interface ILeafPlan {
  operator: TFilterLeafOperator;
  operand: unknown;
  negate: boolean;
  /** Negations must not match a missing field: at runtime "can't tell" is false (§5.2). */
  requirePresent: boolean;
}

function planLeaf(condition: TFlowCondition): ILeafPlan {
  const value = condition.value;
  const plain = (operator: TFilterLeafOperator, operand: unknown = value): ILeafPlan => ({
    operator,
    operand,
    negate: false,
    requirePresent: false,
  });
  const negated = (operator: TFilterLeafOperator): ILeafPlan => ({
    operator,
    operand: value,
    negate: true,
    requirePresent: true,
  });

  switch (condition.op) {
    case EConditionOp.IS:
      return plain('$eq');
    case EConditionOp.IS_NOT:
      return negated('$eq');
    case EConditionOp.CONTAINS:
      return plain('$contains');
    case EConditionOp.NOT_CONTAINS:
      return negated('$contains');
    case EConditionOp.CONTAINS_ANY:
      return plain('$containsAny');
    case EConditionOp.STARTS_WITH:
      return plain('$startsWith');
    case EConditionOp.ENDS_WITH:
      return plain('$endsWith');
    case EConditionOp.MATCHES:
      return plain('$matches');
    case EConditionOp.IS_ONE_OF:
      return plain('$in');
    case EConditionOp.IS_NOT_ONE_OF:
      return negated('$in');
    case EConditionOp.GT:
      return plain('$gt');
    case EConditionOp.GTE:
      return plain('$gte');
    case EConditionOp.LT:
      return plain('$lt');
    case EConditionOp.LTE:
      return plain('$lte');
    case EConditionOp.BETWEEN:
      return plain('$between');
    case EConditionOp.IS_TRUE:
      return plain('$eq', true);
    case EConditionOp.IS_FALSE:
      return plain('$eq', false);
    case EConditionOp.HAS_MORE_THAN:
      return plain('$sizeGt');
    case EConditionOp.IS_EMPTY:
      return plain('$empty', true);
    case EConditionOp.IS_NOT_EMPTY:
      return plain('$empty', false);
    case EConditionOp.EXISTS:
      return plain('$exist', true);
    case EConditionOp.NOT_EXISTS:
      return plain('$exist', false);
  }
}

/** One condition as a Filter-DSL expression over the node-context path. */
function compileCondition(condition: TFlowCondition): Record<string, unknown> {
  const plan = planLeaf(condition);
  const test = { [plan.operator]: plan.operand };
  const body: Record<string, unknown> = plan.negate ? { $not: test } : { ...test };
  if (plan.requirePresent) body.$exist = true;
  return { [condition.path]: body };
}

/** For event search only: nodes evaluate the form itself (§5.2). */
export function compileConditionForm(form: TFlowConditionForm): Record<string, unknown> {
  return {
    $or: form.groups.map((group) => ({ $and: group.conditions.map(compileCondition) })),
  };
}

export type TTriState = 'true' | 'false' | 'unknown';

export interface IConditionVerdict {
  result: TTriState;
  actual?: unknown;
  /** Why the editor "can't tell": the path is missing, or the value is of another type. */
  reason?: string;
}

export interface IConditionFormVerdict {
  result: TTriState;
  groups: { result: TTriState; conditions: IConditionVerdict[] }[];
}

const PRESENCE_OPS: readonly EConditionOp[] = [
  EConditionOp.EXISTS,
  EConditionOp.NOT_EXISTS,
  EConditionOp.IS_EMPTY,
  EConditionOp.IS_NOT_EMPTY,
];

export function evaluateCondition(
  condition: TFlowCondition,
  ctx: IFlowNodeContext,
): IConditionVerdict {
  let actual: unknown;
  try {
    actual = resolveContextPath(ctx, condition.path);
  } catch (error) {
    return { result: 'unknown', reason: error instanceof Error ? error.message : String(error) };
  }
  const plan = planLeaf(condition);
  const shown = actual === FILTER_ABSENT ? undefined : actual;

  if (!PRESENCE_OPS.includes(condition.op)) {
    if (actual === FILTER_ABSENT) {
      return { result: 'unknown', reason: `no ${condition.path} here` };
    }
    const seen = kindOf(actual);
    if (seen !== condition.kind) {
      return { result: 'unknown', actual: shown, reason: `${seen}, not ${condition.kind}` };
    }
  }

  const held =
    condition.kind === EConditionKind.LIST && Array.isArray(actual) && MEMBERSHIP[plan.operator]
      ? listHas(actual, condition.op === EConditionOp.CONTAINS_ANY ? plan.operand : [plan.operand])
      : applyFilterOperator(plan.operator, actual, plan.operand);
  return { result: held !== plan.negate ? 'true' : 'false', actual: shown };
}

const MEMBERSHIP: Partial<Record<TFilterLeafOperator, true>> = {
  $contains: true,
  $containsAny: true,
};

// Loose on purpose (§5.2): ids arrive as 42 from one API and "42" from the next, and a
// list of tags typed in the editor is always text. Search keeps the DSL's strict match.
function listHas(list: unknown[], needles: unknown): boolean {
  return (needles as unknown[]).some((needle) => list.some((item) => looseEqual(item, needle)));
}

function looseEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'string') return b.trim() !== '' && Number(b) === a;
  if (typeof a === 'string' && typeof b === 'number') return looseEqual(b, a);
  return false;
}

export function evaluateConditionForm(
  form: TFlowConditionForm,
  ctx: IFlowNodeContext,
): IConditionFormVerdict {
  const groups = form.groups.map((group) => {
    const conditions = group.conditions.map((condition) => evaluateCondition(condition, ctx));
    return { result: all(conditions.map((c) => c.result)), conditions };
  });
  return { result: any(groups.map((g) => g.result)), groups };
}

/** Runtime reading of a verdict: an unknown never lets an event through. */
export function conditionHolds(verdict: { result: TTriState }): boolean {
  return verdict.result === 'true';
}

function all(results: TTriState[]): TTriState {
  if (results.includes('false')) return 'false';
  return results.includes('unknown') ? 'unknown' : 'true';
}

function any(results: TTriState[]): TTriState {
  if (results.includes('true')) return 'true';
  return results.includes('unknown') ? 'unknown' : 'false';
}

function kindOf(value: unknown): string {
  if (Array.isArray(value)) return EConditionKind.LIST;
  if (value === null) return 'null';
  switch (typeof value) {
    case 'string':
      return EConditionKind.TEXT;
    case 'number':
      return EConditionKind.NUMBER;
    case 'boolean':
      return EConditionKind.BOOLEAN;
    default:
      return 'object';
  }
}
